/**
 * Google Drive and OneDrive, as places a spreadsheet can come from.
 *
 * Both work entirely in this tab, which is the same rule the rest of the upload path
 * follows. The person signs in to Google or Microsoft in a popup that belongs to
 * them, picks a file in the vendor's own picker, and the file's bytes come down into
 * this tab and go through `@broadpaper/ingest` exactly as a dropped file does. The
 * service is told the file's *address* — provider, id, name — so the set of data can
 * be refreshed from it next month; it is never given anything that opens the file.
 * The access token lives in this module's memory and goes when the tab does.
 *
 * That is also what keeps the permission small. Google's `drive.file` covers only
 * the files a person picked with this application, which needs no security
 * assessment and asks the person for nothing they would hesitate over; reading the
 * file again later works because that grant is per file and persists.
 *
 * Each provider is its own chunk, loaded when a screen that offers it opens rather
 * than when it is clicked: a popup opened after an `await` of a network request has
 * lost the click that allowed it, and browsers block it.
 */
import { helpUrl, type DatasetLink } from "../api.js";

export type Provider = DatasetLink["provider"];

/** Where a file is, without which sheet of it was chosen — that is decided after reading. */
export type FileLink = Omit<DatasetLink, "sheet">;

export interface Fetched {
  bytes: Uint8Array;
  /** The name it has now, which is what ingest labels the data with. */
  filename: string;
  link: FileLink;
}

/** What each provider's chunk provides. */
export interface ProviderModule {
  /** Loads the vendor's scripts, so a later click can open a popup straight away. */
  prepare(): Promise<void>;
  /** Null when the person closed the picker without choosing. */
  pick(maxBytes: number): Promise<Fetched | null>;
  fetch(link: FileLink, maxBytes: number): Promise<Fetched>;
}

export { AnotherClick, Cancelled, ConnectorError } from "./errors.js";

export const CONNECTOR_HELP = helpUrl("/support/importing-data/#linked-files");

const env = import.meta.env as Record<string, string | undefined>;

/**
 * Which services this build can offer.
 *
 * Each needs an app registered with the vendor, and a build without one — a
 * developer's machine, a preview deployment — simply does not show the button, rather
 * than showing one that fails after a popup.
 */
export const PROVIDERS: Record<Provider, { name: string; configured: boolean }> = {
  "google-drive": { name: "Google Drive", configured: Boolean(env.VITE_GOOGLE_CLIENT_ID && env.VITE_GOOGLE_API_KEY && env.VITE_GOOGLE_APP_ID) },
  onedrive: { name: "OneDrive", configured: Boolean(env.VITE_MICROSOFT_CLIENT_ID) }
};

export function available(): Provider[] {
  return (Object.keys(PROVIDERS) as Provider[]).filter((p) => PROVIDERS[p].configured);
}

const loaded = new Map<Provider, Promise<ProviderModule>>();

function load(provider: Provider): Promise<ProviderModule> {
  let module = loaded.get(provider);
  if (!module) {
    module = (provider === "google-drive" ? import("./google.js") : import("./microsoft.js")).then((m) => m.default);
    loaded.set(provider, module);
    // A failed load is forgotten, so the next click tries again rather than failing for ever.
    module.catch(() => loaded.delete(provider));
  }
  return module;
}

/** Called when a screen that offers a provider opens. Failures surface on the click instead. */
export function prepare(provider: Provider): void {
  void load(provider)
    .then((m) => m.prepare())
    .catch(() => undefined);
}

export async function pickFile(provider: Provider, maxBytes: number): Promise<Fetched | null> {
  return (await load(provider)).pick(maxBytes);
}

export async function fetchLinked(link: FileLink, maxBytes: number): Promise<Fetched> {
  return (await load(link.provider)).fetch(link, maxBytes);
}
